import { useState, useEffect, Suspense, lazy } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ActivityIndicator, Platform, Alert } from 'react-native';
import { Home as HomeIcon, MapPin, Calendar, Wrench, X, Search, Filter, ExternalLink, ChevronDown, ChevronUp, ArrowUpDown, ArrowUp, ArrowDown, Trash2 } from '../../lib/icons';
import { FileText } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { shadows } from '../../lib/styles';
import { useNavigate } from 'react-router-dom';
import WebScrollView from '../../components/WebScrollView';
import type { MapHome } from '../../components/HomesMap';

// Lazy-load the map only on web — Leaflet uses browser DOM APIs not available on native
const HomesMap = Platform.OS === 'web'
  ? lazy(() => import('../../components/HomesMap'))
  : null;

type SortBy = 'date_created' | 'activity';
type SortDirection = 'asc' | 'desc';

interface Home {
  id: string;
  owner_id: string | null;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  market_id: string | null;
  market_name: string | null;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
  last_activity_date: string | null;
  property_records_count: number;
  visits_count: number;
  service_requests_count: number;
  signed_up_by: string | null;
}

interface Market {
  id: string;
  name: string;
}

interface TeamMember {
  id: string;
  full_name: string;
}

const formatLastActivity = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  const years = Math.floor(diffDays / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
};

const HomesTab = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [homes, setHomes] = useState<Home[]>([]);
  const [filteredHomes, setFilteredHomes] = useState<Home[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Map expand/collapse
  const [mapExpanded, setMapExpanded] = useState(false);

  // Sort states
  const [sortBy, setSortBy] = useState<SortBy>('date_created');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [sortExpanded, setSortExpanded] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMarketId, setSelectedMarketId] = useState<string | 'none' | null>(null);
  const [minPropertyRecords, setMinPropertyRecords] = useState<string>('');
  const [maxPropertyRecords, setMaxPropertyRecords] = useState<string>('');
  const [minVisits, setMinVisits] = useState<string>('');
  const [maxVisits, setMaxVisits] = useState<string>('');
  const [minServiceRequests, setMinServiceRequests] = useState<string>('');
  const [maxServiceRequests, setMaxServiceRequests] = useState<string>('');
  const [selectedSignedUpBy, setSelectedSignedUpBy] = useState<string | 'none' | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [deletingHomeId, setDeletingHomeId] = useState<string | null>(null);

  useEffect(() => {
    fetchMarkets();
    fetchTeamMembers();
    fetchHomes();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [homes, searchQuery, selectedMarketId, selectedSignedUpBy, minPropertyRecords, maxPropertyRecords, minVisits, maxVisits, minServiceRequests, maxServiceRequests, sortBy, sortDirection]);

  const fetchTeamMembers = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('is_system_manager', true)
        .order('full_name', { ascending: true });

      if (fetchError) throw fetchError;
      setTeamMembers(data || []);
    } catch (err: any) {
      console.error('Error fetching team members:', err);
    }
  };

  const fetchMarkets = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('markets')
        .select('id, name')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;
      setMarkets(data || []);
    } catch (err: any) {
      console.error('Error fetching markets:', err);
    }
  };

  const fetchHomes = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all homes with market info
      const { data: homesData, error: homesError } = await supabase
        .from('homes')
        .select(`
          id,
          owner_id,
          address,
          city,
          state,
          zip_code,
          market_id,
          latitude,
          longitude,
          created_at,
          signed_up_by,
          markets (
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (homesError) throw homesError;

      // Fetch counts for each home
      const homeIds = (homesData || []).map(h => h.id);

      // Get property records counts
      const { data: propertyRecordsData } = await supabase
        .from('property_records')
        .select('home_id')
        .in('home_id', homeIds);

      // Get visits counts + most recent scheduled date
      const { data: visitsData } = await supabase
        .from('visits')
        .select('home_id, scheduled_date')
        .in('home_id', homeIds);

      // Get service requests counts + most recent created date
      const { data: serviceRequestsData } = await supabase
        .from('service_requests')
        .select('home_id, created_at')
        .in('home_id', homeIds);

      // Count records per home
      const propertyRecordsCounts = new Map<string, number>();
      (propertyRecordsData || []).forEach(record => {
        propertyRecordsCounts.set(record.home_id, (propertyRecordsCounts.get(record.home_id) || 0) + 1);
      });

      const visitsCounts = new Map<string, number>();
      const lastVisitDates = new Map<string, string>();
      (visitsData || []).forEach(visit => {
        visitsCounts.set(visit.home_id, (visitsCounts.get(visit.home_id) || 0) + 1);
        if (visit.scheduled_date) {
          const current = lastVisitDates.get(visit.home_id);
          if (!current || visit.scheduled_date > current) {
            lastVisitDates.set(visit.home_id, visit.scheduled_date);
          }
        }
      });

      const serviceRequestsCounts = new Map<string, number>();
      const lastServiceRequestDates = new Map<string, string>();
      (serviceRequestsData || []).forEach(request => {
        serviceRequestsCounts.set(request.home_id, (serviceRequestsCounts.get(request.home_id) || 0) + 1);
        if (request.created_at) {
          const current = lastServiceRequestDates.get(request.home_id);
          if (!current || request.created_at > current) {
            lastServiceRequestDates.set(request.home_id, request.created_at);
          }
        }
      });

      // Combine data
      const homesWithCounts: Home[] = (homesData || []).map(home => ({
        id: home.id,
        owner_id: home.owner_id ?? null,
        address: home.address,
        city: home.city,
        state: home.state,
        zip_code: home.zip_code,
        market_id: home.market_id,
        latitude: (home as any).latitude ?? null,
        longitude: (home as any).longitude ?? null,
        created_at: (home as any).created_at || '',
        last_activity_date: (() => {
          const lastVisit = lastVisitDates.get(home.id) || null;
          const lastRequest = lastServiceRequestDates.get(home.id) || null;
          if (!lastVisit && !lastRequest) return null;
          if (!lastVisit) return lastRequest;
          if (!lastRequest) return lastVisit;
          return lastVisit > lastRequest ? lastVisit : lastRequest;
        })(),
        market_name: Array.isArray(home.markets) && home.markets.length > 0 
          ? home.markets[0].name 
          : (home.markets as any)?.name || null,
        signed_up_by: (home as any).signed_up_by ?? null,
        property_records_count: propertyRecordsCounts.get(home.id) || 0,
        visits_count: visitsCounts.get(home.id) || 0,
        service_requests_count: serviceRequestsCounts.get(home.id) || 0,
      }));

      setHomes(homesWithCounts);
    } catch (err: any) {
      console.error('Error fetching homes:', err);
      setError(err.message || 'Failed to load homes');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...homes];

    // Search filter
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(home => {
        const addressMatch = home.address.toLowerCase().includes(searchLower);
        const cityMatch = home.city.toLowerCase().includes(searchLower);
        const stateMatch = home.state.toLowerCase().includes(searchLower);
        const zipMatch = home.zip_code.toLowerCase().includes(searchLower);
        return addressMatch || cityMatch || stateMatch || zipMatch;
      });
    }

    // Market filter
    if (selectedMarketId === 'none') {
      filtered = filtered.filter(home => home.market_id === null);
    } else if (selectedMarketId) {
      filtered = filtered.filter(home => home.market_id === selectedMarketId);
    }

    // Attribution filter
    if (selectedSignedUpBy === 'none') {
      filtered = filtered.filter(home => home.signed_up_by === null);
    } else if (selectedSignedUpBy) {
      filtered = filtered.filter(home => home.signed_up_by === selectedSignedUpBy);
    }

    // Property records count filter
    if (minPropertyRecords) {
      const min = parseInt(minPropertyRecords);
      if (!isNaN(min)) {
        filtered = filtered.filter(home => home.property_records_count >= min);
      }
    }
    if (maxPropertyRecords) {
      const max = parseInt(maxPropertyRecords);
      if (!isNaN(max)) {
        filtered = filtered.filter(home => home.property_records_count <= max);
      }
    }

    // Visits count filter
    if (minVisits) {
      const min = parseInt(minVisits);
      if (!isNaN(min)) {
        filtered = filtered.filter(home => home.visits_count >= min);
      }
    }
    if (maxVisits) {
      const max = parseInt(maxVisits);
      if (!isNaN(max)) {
        filtered = filtered.filter(home => home.visits_count <= max);
      }
    }

    // Service requests count filter
    if (minServiceRequests) {
      const min = parseInt(minServiceRequests);
      if (!isNaN(min)) {
        filtered = filtered.filter(home => home.service_requests_count >= min);
      }
    }
    if (maxServiceRequests) {
      const max = parseInt(maxServiceRequests);
      if (!isNaN(max)) {
        filtered = filtered.filter(home => home.service_requests_count <= max);
      }
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'date_created') {
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else {
        const activityA = a.visits_count + a.service_requests_count;
        const activityB = b.visits_count + b.service_requests_count;
        comparison = activityA - activityB;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    setFilteredHomes(filtered);
  };

  const handleDeleteUnownedHome = (home: Home, e?: { stopPropagation?: () => void }) => {
    e?.stopPropagation?.();
    const label = `${home.address}, ${home.city}`;
    const message = `Remove "${label}"? This cannot be undone.`;

    const performDelete = async () => {
      setDeletingHomeId(home.id);
      setError(null);
      try {
        const { error: deleteError } = await supabase.from('homes').delete().eq('id', home.id);
        if (deleteError) throw deleteError;
        setHomes((prev) => prev.filter((h) => h.id !== home.id));
      } catch (err: any) {
        console.error('Error deleting home:', err);
        setError(err.message || 'Failed to delete home');
      } finally {
        setDeletingHomeId(null);
      }
    };

    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.confirm(message)) {
        void performDelete();
      }
    } else {
      Alert.alert('Delete home', message, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => void performDelete() },
      ]);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedMarketId(null);
    setSelectedSignedUpBy(null);
    setMinPropertyRecords('');
    setMaxPropertyRecords('');
    setMinVisits('');
    setMaxVisits('');
    setMinServiceRequests('');
    setMaxServiceRequests('');
  };

  const hasActiveFilters = selectedMarketId !== null || 
    selectedSignedUpBy !== null ||
    minPropertyRecords !== '' || maxPropertyRecords !== '' || 
    minVisits !== '' || maxVisits !== '' || 
    minServiceRequests !== '' || maxServiceRequests !== '';

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <HomeIcon size={24} color="#111827" />
          <Text style={styles.title}>Homes</Text>
        </View>
        <Pressable
          style={styles.refreshButton}
          onPress={fetchHomes}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator size="small" color="#4b5563" />
          ) : (
            <Text style={styles.refreshButtonText}>Refresh</Text>
          )}
        </Pressable>
      </View>

      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Search size={20} color="#6b7280" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search by address, city, state, or zip..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#9ca3af"
        />
      </View>

      {/* Filters + Sort row */}
      <View style={styles.filtersContainer}>
        <Pressable
          style={styles.filterToggle}
          onPress={() => { setFiltersExpanded(!filtersExpanded); setSortExpanded(false); }}
        >
          <Filter size={16} color="#6b7280" />
          <Text style={styles.filterToggleText}>Filters</Text>
          {hasActiveFilters && (
            <View style={styles.filterBadge}>
              <Text style={styles.filterBadgeText}>
                {[
                  selectedMarketId ? 'Market' : null,
                  selectedSignedUpBy ? 'Attribution' : null,
                  minPropertyRecords || maxPropertyRecords ? 'Records' : null,
                  minVisits || maxVisits ? 'Visits' : null,
                  minServiceRequests || maxServiceRequests ? 'Requests' : null,
                ].filter(Boolean).length}
              </Text>
            </View>
          )}
        </Pressable>

        <Pressable
          style={[styles.filterToggle, sortExpanded && styles.filterToggleActive]}
          onPress={() => { setSortExpanded(!sortExpanded); setFiltersExpanded(false); }}
        >
          <ArrowUpDown size={16} color={sortExpanded ? '#2563eb' : '#6b7280'} />
          <Text style={[styles.filterToggleText, sortExpanded && styles.filterToggleTextActive]}>Sort</Text>
          <View style={styles.sortDirectionChip}>
            {sortDirection === 'asc'
              ? <ArrowUp size={11} color="#6b7280" />
              : <ArrowDown size={11} color="#6b7280" />
            }
            <Text style={styles.sortDirectionChipText}>
              {sortBy === 'date_created' ? 'Date' : 'Activity'}
            </Text>
          </View>
        </Pressable>

        {hasActiveFilters && (
          <Pressable style={styles.clearFiltersButton} onPress={clearFilters}>
            <X size={16} color="#6b7280" />
            <Text style={styles.clearFiltersText}>Clear</Text>
          </Pressable>
        )}
      </View>

      {sortExpanded && (
        <View style={styles.filtersPanel}>
          {/* Sort by */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>Sort by</Text>
            <View style={styles.sortOptions}>
              <Pressable
                style={[styles.sortOption, sortBy === 'date_created' && styles.sortOptionActive]}
                onPress={() => setSortBy('date_created')}
              >
                <Calendar size={15} color={sortBy === 'date_created' ? '#2563eb' : '#6b7280'} />
                <Text style={[styles.sortOptionText, sortBy === 'date_created' && styles.sortOptionTextActive]}>
                  Date Created
                </Text>
              </Pressable>
              <Pressable
                style={[styles.sortOption, sortBy === 'activity' && styles.sortOptionActive]}
                onPress={() => setSortBy('activity')}
              >
                <Wrench size={15} color={sortBy === 'activity' ? '#2563eb' : '#6b7280'} />
                <Text style={[styles.sortOptionText, sortBy === 'activity' && styles.sortOptionTextActive]}>
                  Activity
                </Text>
              </Pressable>
            </View>
          </View>

          {/* Direction */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>Direction</Text>
            <View style={styles.sortOptions}>
              <Pressable
                style={[styles.sortOption, sortDirection === 'desc' && styles.sortOptionActive]}
                onPress={() => setSortDirection('desc')}
              >
                <ArrowDown size={15} color={sortDirection === 'desc' ? '#2563eb' : '#6b7280'} />
                <Text style={[styles.sortOptionText, sortDirection === 'desc' && styles.sortOptionTextActive]}>
                  {sortBy === 'date_created' ? 'Newest first' : 'Most active first'}
                </Text>
              </Pressable>
              <Pressable
                style={[styles.sortOption, sortDirection === 'asc' && styles.sortOptionActive]}
                onPress={() => setSortDirection('asc')}
              >
                <ArrowUp size={15} color={sortDirection === 'asc' ? '#2563eb' : '#6b7280'} />
                <Text style={[styles.sortOptionText, sortDirection === 'asc' && styles.sortOptionTextActive]}>
                  {sortBy === 'date_created' ? 'Oldest first' : 'Least active first'}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      )}

      {filtersExpanded && (
        <View style={styles.filtersPanel}>
          {/* Market Filter */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>Market</Text>
            {Platform.OS === 'web' ? (
              <select
                value={selectedMarketId || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedMarketId(value === '' ? null : value === 'none' ? 'none' : value);
                }}
                style={styles.select}
              >
                <option value="">All Markets</option>
                <option value="none">No Market</option>
                {markets.map((market) => (
                  <option key={market.id} value={market.id}>
                    {market.name}
                  </option>
                ))}
              </select>
            ) : (
              <Text style={styles.selectPlaceholder}>
                {selectedMarketId === 'none' 
                  ? 'No Market' 
                  : selectedMarketId 
                    ? markets.find(m => m.id === selectedMarketId)?.name || 'Market' 
                    : 'All Markets'}
              </Text>
            )}
          </View>

          {/* Attribution (Signed Up By) Filter */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>Attribution (Signed Up By)</Text>
            {Platform.OS === 'web' ? (
              <select
                value={selectedSignedUpBy || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedSignedUpBy(value === '' ? null : value === 'none' ? 'none' : value);
                }}
                style={styles.select}
              >
                <option value="">All</option>
                <option value="none">No attribution</option>
                {teamMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.full_name}
                  </option>
                ))}
              </select>
            ) : (
              <Text style={styles.selectPlaceholder}>
                {selectedSignedUpBy === 'none'
                  ? 'No attribution'
                  : selectedSignedUpBy
                    ? teamMembers.find(m => m.id === selectedSignedUpBy)?.full_name || 'Team member'
                    : 'All'}
              </Text>
            )}
          </View>

          {/* Property Records Count Filter */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>Property Records Count</Text>
            <View style={styles.rangeInputs}>
              <TextInput
                style={styles.rangeInput}
                placeholder="Min"
                value={minPropertyRecords}
                onChangeText={setMinPropertyRecords}
                keyboardType="numeric"
                placeholderTextColor="#9ca3af"
              />
              <Text style={styles.rangeSeparator}>-</Text>
              <TextInput
                style={styles.rangeInput}
                placeholder="Max"
                value={maxPropertyRecords}
                onChangeText={setMaxPropertyRecords}
                keyboardType="numeric"
                placeholderTextColor="#9ca3af"
              />
            </View>
          </View>

          {/* Visits Count Filter */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>Visits Count</Text>
            <View style={styles.rangeInputs}>
              <TextInput
                style={styles.rangeInput}
                placeholder="Min"
                value={minVisits}
                onChangeText={setMinVisits}
                keyboardType="numeric"
                placeholderTextColor="#9ca3af"
              />
              <Text style={styles.rangeSeparator}>-</Text>
              <TextInput
                style={styles.rangeInput}
                placeholder="Max"
                value={maxVisits}
                onChangeText={setMaxVisits}
                keyboardType="numeric"
                placeholderTextColor="#9ca3af"
              />
            </View>
          </View>

          {/* Service Requests Count Filter */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>Service Requests Count</Text>
            <View style={styles.rangeInputs}>
              <TextInput
                style={styles.rangeInput}
                placeholder="Min"
                value={minServiceRequests}
                onChangeText={setMinServiceRequests}
                keyboardType="numeric"
                placeholderTextColor="#9ca3af"
              />
              <Text style={styles.rangeSeparator}>-</Text>
              <TextInput
                style={styles.rangeInput}
                placeholder="Max"
                value={maxServiceRequests}
                onChangeText={setMaxServiceRequests}
                keyboardType="numeric"
                placeholderTextColor="#9ca3af"
              />
            </View>
          </View>
        </View>
      )}

      {/* Map — always shown on web, compact by default with expand option */}
      {Platform.OS === 'web' && HomesMap && (
        <View style={[styles.mapContainer, mapExpanded && styles.mapContainerExpanded]}>
          <Suspense fallback={
            <View style={styles.mapLoadingContainer}>
              <ActivityIndicator size="large" color="#9ca3af" />
            </View>
          }>
            <HomesMap
              homes={filteredHomes.filter((h): h is Home & MapHome => h.latitude !== null && h.longitude !== null)}
              onHomePress={(id) => navigate(`/admin/home?id=${id}`)}
            />
          </Suspense>
          <Pressable
            style={styles.mapExpandButton}
            onPress={() => setMapExpanded(!mapExpanded)}
          >
            {mapExpanded
              ? <ChevronUp size={14} color="#374151" />
              : <ChevronDown size={14} color="#374151" />
            }
            <Text style={styles.mapExpandText}>{mapExpanded ? 'Collapse map' : 'Expand map'}</Text>
          </Pressable>
        </View>
      )}

      {/* Results Count */}
      <View style={styles.resultsHeader}>
        <Text style={styles.resultsText}>
          {filteredHomes.length} home{filteredHomes.length !== 1 ? 's' : ''} found
        </Text>
      </View>

      {/* Homes List */}
      {filteredHomes.length === 0 ? (
        <View style={styles.emptyContainer}>
          <HomeIcon size={48} color="#9ca3af" />
          <Text style={styles.emptyTitle}>No Homes Found</Text>
          <Text style={styles.emptyText}>
            {hasActiveFilters || searchQuery 
              ? 'Try adjusting your filters or search query.' 
              : 'There are no homes in the system yet.'}
          </Text>
        </View>
      ) : (
        <WebScrollView style={styles.list}>
          {filteredHomes.map((home) => (
            <Pressable
              key={home.id}
              style={styles.homeCard}
              onPress={() => navigate(`/admin/home?id=${home.id}`)}
            >
              <View style={styles.homeCardHeader}>
                <View style={styles.homeCardHeaderLeft}>
                  <HomeIcon size={20} color="#2563eb" />
                  <View>
                    <Text style={styles.homeAddress}>{home.address}</Text>
                    <Text style={styles.homeLocation}>
                      {home.city}, {home.state} {home.zip_code}
                    </Text>
                    <View style={styles.lastActivityRow}>
                      <Calendar size={12} color="#9ca3af" />
                      <Text style={styles.lastActivityText}>
                        {home.last_activity_date
                          ? `Last activity ${formatLastActivity(home.last_activity_date)}`
                          : 'No activity yet'}
                      </Text>
                    </View>
                  </View>
                </View>
                <View style={styles.homeCardHeaderRight}>
                  {home.market_name && (
                    <View style={styles.marketBadge}>
                      <MapPin size={14} color="#2563eb" />
                      <Text style={styles.marketBadgeText}>{home.market_name}</Text>
                    </View>
                  )}
                  {Platform.OS === 'web' && (
                    <Pressable
                      style={styles.newTabButton}
                      onPress={(e) => {
                        e.stopPropagation();
                        const url = `${window.location.origin}/admin/home?id=${home.id}`;
                        window.open(url, '_blank');
                      }}
                    >
                      <ExternalLink size={16} color="#6b7280" />
                    </Pressable>
                  )}
                  {home.owner_id === null && (
                    <Pressable
                      style={({ hovered }) => [
                        styles.unownedDeleteButton,
                        hovered && Platform.OS === 'web' ? styles.unownedDeleteButtonHovered : null,
                      ]}
                      onPress={(e) => handleDeleteUnownedHome(home, e)}
                      disabled={deletingHomeId === home.id}
                      accessibilityLabel="Delete unowned home"
                      accessibilityRole="button"
                    >
                      {deletingHomeId === home.id ? (
                        <ActivityIndicator size="small" color="#9ca3af" />
                      ) : (
                        <Trash2 size={15} color="#9ca3af" />
                      )}
                    </Pressable>
                  )}
                </View>
              </View>

              <View style={styles.homeCardStats}>
                <View style={styles.statItem}>
                  <FileText size={16} color="#6b7280" />
                  <Text style={styles.statValue}>{home.property_records_count}</Text>
                  <Text style={styles.statLabel}>Property Records</Text>
                </View>
                <View style={styles.statItem}>
                  <Calendar size={16} color="#6b7280" />
                  <Text style={styles.statValue}>{home.visits_count}</Text>
                  <Text style={styles.statLabel}>Visits</Text>
                </View>
                <View style={styles.statItem}>
                  <Wrench size={16} color="#6b7280" />
                  <Text style={styles.statValue}>{home.service_requests_count}</Text>
                  <Text style={styles.statLabel}>Service Requests</Text>
                </View>
              </View>
            </Pressable>
          ))}
        </WebScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  refreshButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#4b5563',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorContainer: {
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
    marginBottom: 16,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 16,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#111827',
  },
  filtersContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  filterToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  filterToggleActive: {
    backgroundColor: '#eff6ff',
    borderWidth: 1,
    borderColor: '#bfdbfe',
  },
  filterToggleText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  filterToggleTextActive: {
    color: '#2563eb',
  },
  sortDirectionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
  },
  sortDirectionChipText: {
    fontSize: 11,
    fontWeight: '500',
    color: '#6b7280',
  },
  sortOptions: {
    flexDirection: 'row',
    gap: 8,
  },
  sortOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  sortOptionActive: {
    backgroundColor: '#eff6ff',
    borderColor: '#bfdbfe',
  },
  sortOptionText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#6b7280',
  },
  sortOptionTextActive: {
    color: '#2563eb',
  },
  filterBadge: {
    backgroundColor: '#2563eb',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    minWidth: 20,
    alignItems: 'center',
  },
  filterBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#ffffff',
  },
  clearFiltersButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  clearFiltersText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
  },
  filtersPanel: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    gap: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  filterGroup: {
    gap: 8,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  select: {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
    cursor: 'pointer',
  },
  selectPlaceholder: {
    padding: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 14,
    color: '#374151',
    backgroundColor: '#ffffff',
  },
  rangeInputs: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  rangeInput: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  rangeSeparator: {
    fontSize: 16,
    color: '#6b7280',
  },
  resultsHeader: {
    marginBottom: 12,
  },
  resultsText: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: '500',
  },
  mapContainer: {
    height: 220,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 12,
    position: 'relative',
  },
  mapContainerExpanded: {
    height: 480,
  },
  mapLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
  },
  mapExpandButton: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    zIndex: 1000,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
    }),
  },
  mapExpandText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#374151',
  },
  emptyContainer: {
    padding: 60,
    alignItems: 'center',
    gap: 16,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  emptyText: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
  },
  list: {
    flex: 1,
  },
  homeCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    marginBottom: 12,
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
      cursor: 'pointer',
    }),
  },
  homeCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  homeCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
  },
  homeCardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  homeAddress: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  homeLocation: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  lastActivityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 5,
  },
  lastActivityText: {
    fontSize: 12,
    color: '#9ca3af',
  },
  marketBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#eff6ff',
    borderRadius: 6,
  },
  marketBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#2563eb',
  },
  newTabButton: {
    padding: 6,
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  unownedDeleteButton: {
    padding: 6,
    borderRadius: 6,
    minWidth: 28,
    minHeight: 28,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.65,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  unownedDeleteButtonHovered: {
    opacity: 1,
    backgroundColor: '#f3f4f6',
  },
  homeCardStats: {
    flexDirection: 'row',
    gap: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statValue: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
  },
});

export default HomesTab;

